import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Source } from "@/components/api/fiinder-api-types";

type SourceLookupContextValue = {
  findSourceByUrl: (params: { url: string }) => Source | undefined;
};

function normalizeUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, "");
}

const SourceLookupCtx = createContext<SourceLookupContextValue | null>(null);

type SourceLookupProviderProps = {
  sources: Source[];
  children: ReactNode;
};

/**
 * Provider that maps URLs to Source objects for downstream consumers.
 *
 * Wraps message content so that components like Anchor can look up
 * whether a given URL corresponds to a known source (e.g. a PDF).
 */
export function SourceLookupProvider({ sources, children }: SourceLookupProviderProps) {
  const value = useMemo<SourceLookupContextValue>(() => {
    // A markdown href only ever carries the public url, so two distinct
    // documents sharing one (their identity, instance_id, differs) cannot
    // both be resolved from a click -- first occurrence wins, mirroring
    // deduplicateSources' rule, instead of an arbitrary last-write-wins.
    const urlMap = new Map<string, Source>();
    for (const source of sources) {
      const key = source.url ? normalizeUrl(source.url) : null;
      if (key && !urlMap.has(key)) {
        urlMap.set(key, source);
      }
    }
    return {
      findSourceByUrl: ({ url }: { url: string }) => urlMap.get(normalizeUrl(url)),
    };
  }, [sources]);

  return <SourceLookupCtx.Provider value={value}>{children}</SourceLookupCtx.Provider>;
}

/**
 * Returns the source lookup context value, or null if outside a provider.
 */
export function useSourceLookupOptional(): SourceLookupContextValue | null {
  return useContext(SourceLookupCtx);
}
